import * as THREE from "three";

import { sampleTerrain, terrainColor } from "../world/terrain";
import type { RenderQualityId } from "./quality";

const SPAN_M = 560;
const CELL_M = 24;
const TERRAIN_SEGMENTS: Record<Exclude<RenderQualityId, "auto">, number> = {
  high: 160,
  medium: 120,
  low: 96,
};
const WATER_SEGMENTS: Record<Exclude<RenderQualityId, "auto">, number> = {
  high: 160,
  medium: 120,
  low: 96,
};

/** CPU terrain remains authoritative; the material only adds visual response. */
export class TerrainGround {
  public readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshStandardMaterial>;
  private readonly water: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  private readonly mat: THREE.MeshStandardMaterial;
  private readonly waterMat: THREE.ShaderMaterial;
  private quality: Exclude<RenderQualityId, "auto"> = "high";
  private lastOx = Number.POSITIVE_INFINITY;
  private lastOz = Number.POSITIVE_INFINITY;
  private lastSeed = Number.NaN;

  public constructor(scene: THREE.Scene) {
    this.mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.88,
      metalness: 0.015,
      fog: true,
    });
    addTerrainDetail(this.mat);
    this.mesh = new THREE.Mesh(makeTerrainGeometry(TERRAIN_SEGMENTS.high), this.mat);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.receiveShadow = true;
    scene.add(this.mesh);

    this.waterMat = createWaterMaterial();
    this.water = new THREE.Mesh(makeWaterGeometry(WATER_SEGMENTS.high), this.waterMat);
    this.water.rotation.x = -Math.PI / 2;
    this.water.renderOrder = 1;
    this.water.frustumCulled = false;
    scene.add(this.water);
  }

  public setQuality(quality: Exclude<RenderQualityId, "auto">): void {
    if (quality === this.quality) return;
    this.quality = quality;
    this.mesh.geometry.dispose();
    this.mesh.geometry = makeTerrainGeometry(TERRAIN_SEGMENTS[quality]);
    this.water.geometry.dispose();
    this.water.geometry = makeWaterGeometry(WATER_SEGMENTS[quality]);
    this.lastOx = Number.POSITIVE_INFINITY;
    this.lastOz = Number.POSITIVE_INFINITY;
    this.mesh.userData.terrainQuality = quality;
  }

  public sync(seed: number, px: number, pz: number): void {
    const ox = Math.round(px / CELL_M) * CELL_M;
    const oz = Math.round(pz / CELL_M) * CELL_M;
    if (ox === this.lastOx && oz === this.lastOz && seed === this.lastSeed) return;
    this.lastOx = ox;
    this.lastOz = oz;
    this.lastSeed = seed;
    this.rebuild(seed, ox, oz);
  }

  public animate(timeSeconds: number): void {
    this.waterMat.uniforms.uTime!.value = timeSeconds;
  }

  public stats(): { quality: string; vertexCount: number; rebuildMs: number } {
    return {
      quality: this.quality,
      vertexCount: Number(this.mesh.userData.terrainVertexCount ?? 0),
      rebuildMs: Number(this.mesh.userData.terrainRebuildMs ?? 0),
    };
  }

  public dispose(): void {
    this.mesh.geometry.dispose();
    this.mat.dispose();
    this.mesh.removeFromParent();
    this.water.geometry.dispose();
    this.waterMat.dispose();
    this.water.removeFromParent();
  }

  private rebuild(seed: number, ox: number, oz: number): void {
    const started = performance.now();
    const geometry = this.mesh.geometry;
    const position = geometry.attributes.position!;
    const color = geometry.attributes.color!;
    const materialWeights = geometry.attributes.materialWeights!;
    const waterPosition = this.water.geometry.attributes.position!;
    const waterData = this.water.geometry.attributes.waterData!;
    const sharedGrid = waterPosition.count === position.count;
    const tone = new THREE.Color();
    for (let i = 0; i < position.count; i += 1) {
      const wx = ox + position.getX(i);
      const wz = oz - position.getY(i);
      const sample = sampleTerrain(seed, wx, wz);
      position.setZ(i, sample.height);
      tone.setHex(terrainColor(seed, wx, wz, sample));
      color.setXYZ(i, tone.r, tone.g, tone.b);
      materialWeights.setXYZW(i, sample.rock, sample.soil, sample.vegetation, sample.wetSediment);
      if (sharedGrid) {
        const level = sample.waterLevel ?? sample.height;
        waterPosition.setZ(i, level + 0.04);
        waterData.setXY(i, sample.waterMask, Math.max(0, level - sample.height));
      }
    }
    position.needsUpdate = true;
    color.needsUpdate = true;
    materialWeights.needsUpdate = true;
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    this.mesh.position.set(ox, 0, oz);
    this.water.position.set(ox, 0, oz);
    if (sharedGrid) {
      waterPosition.needsUpdate = true;
      waterData.needsUpdate = true;
      this.water.geometry.computeBoundingSphere();
    } else {
      this.rebuildWater(seed, ox, oz);
    }
    this.mesh.userData.terrainVertexCount = position.count;
    this.mesh.userData.terrainRebuildMs = performance.now() - started;
  }

  private rebuildWater(seed: number, ox: number, oz: number): void {
    const position = this.water.geometry.attributes.position!;
    const waterData = this.water.geometry.attributes.waterData!;
    for (let i = 0; i < position.count; i += 1) {
      const wx = ox + position.getX(i);
      const wz = oz - position.getY(i);
      const sample = sampleTerrain(seed, wx, wz);
      const level = sample.waterLevel ?? sample.height;
      position.setZ(i, level + 0.04);
      waterData.setXY(i, sample.waterMask, Math.max(0, level - sample.height));
    }
    position.needsUpdate = true;
    waterData.needsUpdate = true;
    this.water.geometry.computeBoundingSphere();
  }
}

function makeTerrainGeometry(segments: number): THREE.PlaneGeometry {
  const geometry = new THREE.PlaneGeometry(SPAN_M, SPAN_M, segments, segments);
  const count = geometry.attributes.position!.count;
  geometry.setAttribute("color", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  geometry.setAttribute(
    "materialWeights",
    new THREE.BufferAttribute(new Float32Array(count * 4), 4),
  );
  return geometry;
}

function makeWaterGeometry(segments: number): THREE.PlaneGeometry {
  const geometry = new THREE.PlaneGeometry(SPAN_M, SPAN_M, segments, segments);
  const count = geometry.attributes.position!.count;
  geometry.setAttribute("waterData", new THREE.BufferAttribute(new Float32Array(count * 2), 2));
  return geometry;
}

function addTerrainDetail(mat: THREE.MeshStandardMaterial): void {
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        `#include <common>
attribute vec4 materialWeights;
varying vec3 vTerrainWorldPos; varying vec4 vMaterialWeights;`,
      )
      .replace(
        "#include <worldpos_vertex>",
        `#include <worldpos_vertex>
vTerrainWorldPos = worldPosition.xyz; vMaterialWeights = materialWeights;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>
varying vec3 vTerrainWorldPos; varying vec4 vMaterialWeights;
float th(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float tn(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.0-2.0*f);return mix(mix(th(i),th(i+vec2(1.,0.)),f.x),mix(th(i+vec2(0.,1.)),th(i+vec2(1.)),f.x),f.y);}`,
      )
      .replace(
        "#include <color_fragment>",
        `#include <color_fragment>
vec3 terrainNormal=normalize(cross(dFdx(vTerrainWorldPos),dFdy(vTerrainWorldPos)));
float slope=1.0-abs(terrainNormal.y);
float broad=tn(vTerrainWorldPos.xz*.035+31.0);
float grain=tn(vTerrainWorldPos.xz*.28)+tn(vTerrainWorldPos.xz*1.18+17.0)*.42;
float rockResponse=clamp(vMaterialWeights.x+smoothstep(.08,.32,slope)*.42,0.0,1.0);
float soilResponse=vMaterialWeights.y;
float vegetationResponse=vMaterialWeights.z;
float wetResponse=vMaterialWeights.w;
vec3 soilTone=diffuseColor.rgb*mix(.82,1.14,broad)*mix(.90,1.08,grain/1.42);
vec3 rockTone=vec3(.31,.30,.27)*mix(.78,1.18,grain/1.42);
vec3 vegetationTone=diffuseColor.rgb*vec3(.70,.91,.66)*mix(.85,1.12,broad);
vec3 wetTone=diffuseColor.rgb*vec3(.58,.67,.61)*mix(.78,1.02,grain/1.42);
float norm=max(.001,rockResponse+soilResponse+vegetationResponse+wetResponse);
diffuseColor.rgb=(rockTone*rockResponse+soilTone*soilResponse+vegetationTone*vegetationResponse+wetTone*wetResponse)/norm;
float raking=dot(terrainNormal,normalize(vec3(.70,.34,-.60)))*.5+.5;
diffuseColor.rgb*=mix(.76,1.16,raking);`,
      );
  };
  mat.customProgramCacheKey = () => "zonevelocity-terrain-weights-v4";
}

function createWaterMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 } },
    transparent: true,
    depthWrite: false,
    toneMapped: false,
    side: THREE.DoubleSide,
    vertexShader: `attribute vec2 waterData; varying vec2 vWorldXZ; varying float vMask; varying float vDepth;
void main(){vec4 world=modelMatrix*vec4(position,1.);vWorldXZ=world.xz;vMask=waterData.x;vDepth=waterData.y;gl_Position=projectionMatrix*viewMatrix*world;}`,
    fragmentShader: `uniform float uTime; varying vec2 vWorldXZ; varying float vMask; varying float vDepth;
float h(vec2 p){return fract(sin(dot(p,vec2(41.73,289.13)))*19417.31);}float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1.,0.)),f.x),mix(h(i+vec2(0.,1.)),h(i+vec2(1.)),f.x),f.y);}
void main(){float edge=max(fwidth(vMask)*1.5,.015);float alpha=smoothstep(.02+edge,.18+edge,vMask);if(alpha<.01)discard;float waves=n(vWorldXZ*.10+vec2(uTime*.22,-uTime*.15))+n(vWorldXZ*.28+vec2(-uTime*.34,uTime*.25))*.42;float depth=clamp(vDepth/5.,0.,1.);vec3 color=mix(vec3(.075,.27,.30),vec3(.025,.095,.15),depth)+(waves-.48)*vec3(.035,.08,.09);float shore=1.-smoothstep(.04+.5*edge,.32+.7*edge,vMask);float foam=shore*smoothstep(.38,.82,waves);color=mix(color,vec3(.70,.78,.68),foam*.48);gl_FragColor=vec4(color,alpha*(.56+depth*.22));}`,
  });
}
